/* eslint max-lines-per-function: ["error", {max: 175}] -- flag-gated v1 + 3 v2 variant data sources */
/* eslint max-statements: ["error", {max: 26}] -- flag-gated v1 + 3 v2 variant data sources */
/* eslint complexity: ["error", {max: 12}] -- flag-gated v1 + 3 v2 variant data sources */
import { useCallback, useMemo } from "react";

import isEmpty from "lodash/isEmpty";

import {
  GroupClassCarouselVariantValues,
  GroupTypeCode,
} from "@/constants/Schedule";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import {
  useGetGroupClassesQuery,
  useGetGroupsQuery,
  useGetMyRegisteredGroupsQuery,
} from "@/store/api/schedule/groupClasses";
import { useAppUserSelector } from "@/store/reducers/appUser";
import type { GroupClassCardItem } from "@/ui/components/groupClasses/series/GroupClassCard";
import {
  getRelevantGroupClasses,
  sortGroupClassesByRegistrationStatusAndStartDate,
  toGroupClassSeries,
} from "@/utils/groupClass.util";
import {
  excludeSensitiveGroups,
  shouldHideSensitiveGroups as getShouldHideSensitiveGroups,
  toAvailableGroupCardItem,
  toRegisteredGroupCardItem,
} from "@/utils/groupClassV2.util";

const PromotedGroups = GroupClassCarouselVariantValues.promotedGroups;
const YourGroups = GroupClassCarouselVariantValues.yourGroups;

interface UseCarouselDataValuesProps {
  selectedUserId: string;
  variant: GroupClassCarouselVariant;
}

interface UseGroupClassCarouselData {
  data: GroupClassSeries[];
  // flag on: normalized GroupClassCard view-models; undefined when flag off
  groupCardItems?: GroupClassCardItem[];
  isFetching: boolean;
  isLoading: boolean;
  isRecommended: boolean;
  onRefresh: () => void;
  registeredGroupCount: number;
  shouldRenderCarousel: boolean;
  shouldRenderEmptyComponent: boolean;
  shouldRenderSpinner: boolean;
  shouldShowMostPopularText: boolean;
  shouldShowViewAllCard: boolean;
}

const useGroupClassCarouselData = ({
  selectedUserId,
  variant,
}: UseCarouselDataValuesProps): UseGroupClassCarouselData => {
  const { isGroupClassRedesignEnabled } = useFeatureFlags();
  const isYourGroups = variant === YourGroups;
  const isPromotedGroups = variant === PromotedGroups;

  const patientIdForQuery = useScheduleQueryPatientId(selectedUserId);
  const {
    data: groupsData,
    isFetching: isV1Fetching,
    isLoading: isV1Loading,
    refetch,
  } = useGetGroupClassesQuery(
    { patientId: patientIdForQuery },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
    { skip: isGroupClassRedesignEnabled },
  );

  // both basicGroups and promotedGroups source from the v2 groups endpoint; only
  // yourGroups uses my-groups. promotedGroups shows the "core" subset of groups (see
  // groupCardItems below), while basicGroups shows the full list.
  const availableGroupsQuery = useGetGroupsQuery(
    { patientExternalId: patientIdForQuery },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
      skip: !isGroupClassRedesignEnabled || isYourGroups,
    },
  );
  // yourGroups renders from this query; promotedGroups also needs it to count the user's
  // registered (non-orientation) groups for the "Explore groups" blurb
  const myRegisteredGroupsQuery = useGetMyRegisteredGroupsQuery(
    { patientExternalId: patientIdForQuery },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
      skip:
        !isGroupClassRedesignEnabled || (!isYourGroups && !isPromotedGroups),
    },
  );

  const { externalId: appUserId } = useAppUserSelector();

  // sensitive registration is visible only when viewing your OWN group classes, and masked otherwise.
  // this is not restricted to specific variants since otherwise viewing your own "Your Groups" and "Groups" carousels would show a registered sensitive class in both
  // note, also: JN-2039 updated schedule-service to handle masking isRegistered for sensitive group classes on the server-side, so this is technically "redundant"
  const shouldShowSensitiveRegistration = useMemo(
    () => !getShouldHideSensitiveGroups({ appUserId, selectedUserId }),
    [appUserId, selectedUserId],
  );

  const data: GroupClassSeries[] = useMemo(() => {
    const unsortedData = getRelevantGroupClasses({
      groupClasses: groupsData,
      shouldShowSensitiveRegistration,
      variant,
    });

    const sortedData = sortGroupClassesByRegistrationStatusAndStartDate({
      groupClasses: unsortedData,
      shouldShowSensitiveRegistration,
    });

    return sortedData.map(toGroupClassSeries);
  }, [groupsData, shouldShowSensitiveRegistration, variant]);

  // the v2 query backing the current variant (promotedGroups shares the groups query)
  const activeGroupsQuery = isYourGroups
    ? myRegisteredGroupsQuery
    : availableGroupsQuery;

  // card view-models, mapped from whichever v2 endpoint the variant uses
  const getGroupCardItems = (): GroupClassCardItem[] | undefined => {
    if (!isGroupClassRedesignEnabled) return undefined;
    if (isYourGroups) {
      // a user viewing another user's groups must not see their sensitive registrations;
      // when viewing their own groups, sensitive ones are shown
      const registeredGroups = shouldShowSensitiveRegistration
        ? (myRegisteredGroupsQuery.data ?? [])
        : excludeSensitiveGroups(myRegisteredGroupsQuery.data ?? []);
      return registeredGroups.map(toRegisteredGroupCardItem);
    }
    const availableGroups = availableGroupsQuery.data ?? [];
    // promotedGroups shows only the "core" groups; basicGroups shows all groups
    return (
      isPromotedGroups
        ? availableGroups.filter((group) => group.isCore)
        : availableGroups
    ).map(toAvailableGroupCardItem);
  };
  const groupCardItems = getGroupCardItems();

  // effective loading/fetching reflects the active data source (v1 vs v2)
  const isLoading = isGroupClassRedesignEnabled
    ? activeGroupsQuery.isLoading
    : isV1Loading;
  const isFetching = isGroupClassRedesignEnabled
    ? activeGroupsQuery.isFetching
    : isV1Fetching;

  const onRefresh = useCallback(() => {
    void (isGroupClassRedesignEnabled
      ? activeGroupsQuery.refetch()
      : refetch());
  }, [activeGroupsQuery, isGroupClassRedesignEnabled, refetch]);

  const isForSupportPatient = useMemo(
    () => !isEmpty(selectedUserId) && selectedUserId !== appUserId,
    [appUserId, selectedUserId],
  );

  const emptinessProps = useMemo(() => {
    const activeContent = groupCardItems ?? data;
    const hasContent = !isEmpty(activeContent) || isLoading || isFetching;
    const shouldRenderEmptyComponent = variant === YourGroups && !hasContent;

    const shouldRenderCarousel =
      hasContent ||
      shouldRenderEmptyComponent ||
      // if support user switches to a patient who has nothing to show for content,
      // the carousel should still be rendered to prevent it disappearing after selecting that patient
      isForSupportPatient;
    return {
      shouldRenderCarousel,
      shouldRenderEmptyComponent,
    };
  }, [
    data,
    isFetching,
    isLoading,
    isForSupportPatient,
    groupCardItems,
    variant,
  ]);

  const { shouldRenderCarousel, shouldRenderEmptyComponent } = emptinessProps;

  // registered-group count backs the v2-only "Explore groups" blurb (promotedGroups),
  // counted from the user's registered groups. Orientation groups don't count toward
  // engagement. my-groups already returns only registered groups (deduped by groupId).
  const registeredGroupCount =
    myRegisteredGroupsQuery.data?.filter(
      (group) => group.groupTypeCode !== GroupTypeCode.Orientation,
    ).length ?? 0;

  return {
    data,
    groupCardItems,
    isFetching,
    isLoading,
    isRecommended: variant === PromotedGroups,
    onRefresh,
    registeredGroupCount,
    shouldRenderCarousel,
    shouldRenderEmptyComponent,
    // changing the selected user sets isFetching to true, but not isLoading
    shouldRenderSpinner: isFetching,
    shouldShowMostPopularText: variant === PromotedGroups,
    // for yourGroups variant, all relevant groups are already shown in carousel
    shouldShowViewAllCard: variant !== YourGroups,
  };
};

export default useGroupClassCarouselData;
